/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import dayjs from "dayjs";
import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { ContactApi } from "../../api/Contacts";
import { NoteAPI } from "../../api/Note";
import { NoteCategoriesAPI } from "../../api/NoteCategory";
import { addEmailRestrictions } from "../../api/User";
import { ContactFilterAtom } from "../../atoms/ContactFilterAtom";
import { ContactFiltersAtom } from "../../atoms/ContactFiltersAtom";
import { ContactsAtom } from "../../atoms/ContactsAtom";
import { ContactNotesAtom, NoteCategoriesAtom } from "../../atoms/NotesAtom";
import { UserAtom } from "../../atoms/UserAtom";
import { ContactStatus, IUserEmailRestrictions } from "../../generated/Api";
import { IContact } from "../../types/Contact";
import { INoteType } from "../../types/NoteCategory";
import { ContactNote } from "../../types/NoteTypes";
import {
  localStorageGetItem,
  localStorageSetItem,
} from "../../utils/LocalStorage";
import Loading from "../Loading";
import { useModal } from "../Modal/Modal";
import AutoCompleteNoteCategory from "../Notes/AutoCompleteNoteCategory";
import StyledButton from "../StyledButton";
import StyledCheckBox from "../StyledCheckBox";
import StyledTextArea from "../StyledTextArea";

const Wrapper = styled.div`
  width: 500px;
  height: 80%;
  overflow-y: auto;
  margin: 15px -25px 25px -25px;
  padding: 30px 30px 0px 30px;
  border-top: 1px solid #eceeee;
  border-bottom: 1px solid #eceeee;
`;

const BlockOptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const BlockOptionItem = styled.div`
  display: flex;
  grid-gap: 15px;
  margin-left: -4px;
  margin-bottom: 12px;
`;

const BottomDiv = styled.div`
  display: flex;
  justify-content: flex-end;
  grid-gap: 15px;
`;

const DetailDiv = styled.span`
  color: #888888;
  margin-left: 5px;
`;

const InputWrapper = styled.div`
  margin: 30px 0;
`;

const Title = styled.div`
  color: #2f2f2f;
  font-weight: 600;
`;

interface Props {
  contacts: IContact[];
  selectAll?: boolean;
}

export default function BlockContactModal(props: Props) {
  const { contacts, selectAll } = props;
  const [loading, setLoading] = useState(false);
  const [loadingNotes, setLoadingNotes] = useState(true);
  const [user, setUser] = useRecoilState(UserAtom);
  const setContacts = useSetRecoilState(ContactsAtom);
  const modal = useModal();
  const setNotes = useSetRecoilState(ContactNotesAtom);
  const [isManuallyCreatedContact, setIsManuallyCreatedContact] =
    useState(false);
  const [userNote, setUserNote] = useState("");
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);

  const lastBlockNoteCategoryName = localStorageGetItem(
    "LAST_NOTE_CATEGORY"
  ) as string;

  const [categoryInputValue, setCategoryInputValue] = useState(
    lastBlockNoteCategoryName || ""
  );

  const [deviceIdsToBlock, setDeviceIdsToBlock] = useState<string[]>([]);

  const [deviceIpsToBlock, setDeviceIpsToBlock] = useState<string[]>([]);
  const [phonesToBlock, setPhonesToBlock] = useState<string[]>([]);

  const contactFilter = useRecoilValue(ContactFilterAtom);
  const contactFilters = useRecoilValue(ContactFiltersAtom);
  const [categories, setCategories] = useRecoilState(NoteCategoriesAtom);
  const [categorySelected, setCategorySelected] = useState<{
    id: string;
    name: string;
    type: string;
  }>({
    id: "",
    name: "",
    type: "",
  });
  const [suggestions, setSuggestions] = useState<
    { id: string; name: string; type: INoteType }[]
  >([]);

  const [blockOptions, setBlockOptions] = useState({
    deviceIp: false,
    deviceId: false,
    phone: false,
    email: false,
  });

  useEffect(() => {
    if (textAreaRef.current) textAreaRef.current.focus();
  }, []);

  async function checkBlock(contacts: IContact[]) {
    const responseFromIpify = await fetch("https://api.ipify.org/?format=json");

    const { ip } = await responseFromIpify.json();

    const deviceIdFromStorage = localStorage.getItem("@deviceId") as string;

    const phonesArrayToBlock = contacts.map((c) => c.phone).filter((p) => p);

    setPhonesToBlock(phonesArrayToBlock);

    const deviceIdsArrayToBlock = contacts
      .map((contact) =>
        contact.deviceIds
          ? contact.deviceIds.filter(
              (deviceId) =>
                deviceId !== deviceIdFromStorage &&
                deviceId !== "system" &&
                deviceId
            )
          : []
      )
      .flat();

    setDeviceIdsToBlock(deviceIdsArrayToBlock);

    const deviceIpsArrayToBlock = contacts
      .map((contact) => contact.lastIp)
      .filter((lastIp) => lastIp !== ip && lastIp !== "system" && lastIp);

    setDeviceIpsToBlock(deviceIpsArrayToBlock);

    let newBlockOptions = {
      deviceIp: !!deviceIpsArrayToBlock.length,
      deviceId: !!deviceIdsArrayToBlock.length,
      phone: !!phonesArrayToBlock.length,
      email: true,
    };

    setBlockOptions(newBlockOptions);
  }

  useEffect(() => {
    checkBlock(contacts);
  }, [contacts]);

  useEffect(() => {
    (async () => {
      const categoriesResponse = await NoteCategoriesAPI.readAll();

      if (categoriesResponse) {
        setCategories(categoriesResponse);

        if (lastBlockNoteCategoryName) {
          const foundNoteCategory = categoriesResponse.find(
            (category) => category.name === lastBlockNoteCategoryName
          );
          if (foundNoteCategory) {
            setCategorySelected({
              id: foundNoteCategory._id?.toString() || "",
              name: foundNoteCategory.name,
              type: foundNoteCategory.type,
            });
          }
        }
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const checkManuallyCreated = await ContactApi.checkUserCreatedContacts({
          contactIds: contacts.map((c) => c._id),
        });

        setIsManuallyCreatedContact(checkManuallyCreated);
      } catch (err) {
        console.log("error", err);
        setIsManuallyCreatedContact(true);
      } finally {
        setLoadingNotes(false);
      }
    })();
  }, [contacts]);

  function checkBlockOptions() {
    let valid = false;

    Object.values(blockOptions).map((v) => {
      if (v === true) valid = true;
    });

    return !valid;
  }

  async function handleCategoryValueSelect(option: any) {
    if (option) {
      setCategorySelected(option);
      setCategoryInputValue(option.name);
    }
  }

  useEffect(() => {
    if (!categories) return;

    const newCategories = categories.map((c) => ({
      id: c._id?.toString() || "",
      name: c.name,
      type: c.type,
    }));

    setSuggestions(newCategories);
  }, [categories]);

  async function createNote() {
    if (contacts.length === 1) {
      try {
        const note = await NoteAPI.createUserNote({
          noteCategoryId: categorySelected.id,
          note: userNote,
          contactId: contacts[0]._id,
        });

        if (note) {
          const newNotes = await NoteAPI.getNotesFromContact(contacts[0]._id);
          if (!newNotes) return;

          const sortedNotes = newNotes.sort((a, b) => {
            return dayjs(b.createdAt).diff(a.createdAt);
          });

          setNotes(sortedNotes);

          localStorageSetItem("LAST_NOTE_CATEGORY", categorySelected.name);

          toast("Note created", { type: "success" });
          modal.popModal();
        }
      } catch (e) {
        toast("Error on create note", { type: "warning" });
      }
    } else {
      try {
        let note: false | ContactNote;

        if (selectAll) {
          note = await NoteAPI.createUserNoteOnContactsSelected({
            noteCategoryId: categorySelected.id,
            note: userNote,
            query: { ...contactFilters, tagQuery: contactFilter?.tagQuery },
          });
        } else {
          note = await NoteAPI.createUserNoteOnContactsSelected({
            noteCategoryId: categorySelected.id,
            note: userNote,
            contactIds: contacts.map((ct) => ct._id),
          });
        }

        if (note) {
          toast("Notes created", { type: "success" });
          modal.popModal();
        }
      } catch (e) {
        toast("Error on create note", { type: "warning" });
      }
    }
  }

  async function handleBlockUsers() {
    setLoading(true);
    const userRestrictions = { ...user.emailRestrictions };
    let restrictions: Partial<IUserEmailRestrictions> = {};

    if (blockOptions.deviceIp && !isManuallyCreatedContact) {
      restrictions.deviceIpsBlocked = deviceIpsToBlock;

      userRestrictions.deviceIpsBlocked = Array.from(
        new Set([
          ...userRestrictions.deviceIpsBlocked,
          ...restrictions.deviceIpsBlocked,
        ])
      );
    }
    if (blockOptions.deviceId && !isManuallyCreatedContact) {
      restrictions.deviceIdsBlocked = deviceIdsToBlock;

      userRestrictions.deviceIdsBlocked = Array.from(
        new Set([
          ...userRestrictions.deviceIdsBlocked,
          ...restrictions.deviceIdsBlocked,
        ])
      );
    }
    if (blockOptions.email) {
      restrictions.emailsBlocked = contacts.map((c) => c.email);
      userRestrictions.emailsBlocked = Array.from(
        new Set([
          ...userRestrictions.emailsBlocked,
          ...restrictions.emailsBlocked,
        ])
      );
    }
    if (blockOptions.phone) {
      restrictions.phoneNumbersBlocked = phonesToBlock;
      userRestrictions.phoneNumbersBlocked = Array.from(
        new Set([
          ...userRestrictions.phoneNumbersBlocked,
          ...restrictions.phoneNumbersBlocked,
        ])
      );
    }

    try {
      await addEmailRestrictions(restrictions);
      await ContactApi.manyAdminSuspend({
        contactIds: contacts.map((c) => c._id),
      });
      setUser({ ...user, emailRestrictions: userRestrictions });

      setContacts((contactsFromAtom) =>
        contactsFromAtom.map((cFromAtom) => {
          if (contacts.find((contact) => contact._id === cFromAtom._id)) {
            return { ...cFromAtom, status: ContactStatus.SUSPENDED };
          }
          return cFromAtom;
        })
      );

      if (userNote && categoryInputValue) createNote();

      modal.popModal();
      toast("Success", { type: "success" });
    } catch (err) {
      toast("Unexpected Error", { type: "error" });
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <Wrapper>
        {loadingNotes ? (
          <div style={{ display: "flex" }}>
            <Loading />
          </div>
        ) : (
          <BlockOptionsContainer>
            {!!deviceIpsToBlock.length && !isManuallyCreatedContact && (
              <BlockOptionItem>
                <StyledCheckBox
                  handleCheck={() =>
                    setBlockOptions({
                      ...blockOptions,
                      deviceIp: !blockOptions.deviceIp,
                    })
                  }
                  check={blockOptions.deviceIp}
                />
                <div>
                  Block Device Ip{" "}
                  <DetailDiv>
                    (
                    {deviceIpsToBlock.length > 1
                      ? "Many Entries"
                      : deviceIpsToBlock[0]}
                    )
                  </DetailDiv>
                </div>
              </BlockOptionItem>
            )}
            {!!deviceIdsToBlock.length && !isManuallyCreatedContact && (
              <BlockOptionItem>
                <StyledCheckBox
                  handleCheck={() =>
                    setBlockOptions({
                      ...blockOptions,
                      deviceId: !blockOptions.deviceId,
                    })
                  }
                  check={blockOptions.deviceId}
                />
                <div>
                  Block Device ID
                  <DetailDiv>
                    (
                    {deviceIdsToBlock.length > 1
                      ? "Many Entries"
                      : deviceIdsToBlock[0]}
                    )
                  </DetailDiv>
                </div>
              </BlockOptionItem>
            )}
            {!!phonesToBlock.length && (
              <BlockOptionItem>
                <StyledCheckBox
                  handleCheck={() =>
                    setBlockOptions({
                      ...blockOptions,
                      phone: !blockOptions.phone,
                    })
                  }
                  check={blockOptions.phone}
                />
                <div>
                  Block Phone
                  <DetailDiv>
                    ({contacts.length > 1 ? "Many Entries" : contacts[0].phone})
                  </DetailDiv>
                </div>
              </BlockOptionItem>
            )}
            <BlockOptionItem>
              <StyledCheckBox
                handleCheck={() =>
                  setBlockOptions({
                    ...blockOptions,
                    email: !blockOptions.email,
                  })
                }
                check={blockOptions.email}
              />
              <div>
                Block Email
                <DetailDiv>
                  ({contacts.length > 1 ? "Many Entries" : contacts[0].email})
                </DetailDiv>
              </div>
            </BlockOptionItem>

            <InputWrapper>
              <Title>Note</Title>
              <StyledTextArea
                ref={textAreaRef}
                value={userNote}
                onChange={(e) => setUserNote(e.target.value)}
                placeholder="Enter the note"
                css={css`
                  margin-top: 5px;
                `}
              />

              <Title
                css={css`
                  margin: 20px 0px 5px;
                `}
              >
                Note Category
              </Title>
              <AutoCompleteNoteCategory
                selectedValue={categoryInputValue}
                onValueSelect={(option) => handleCategoryValueSelect(option)}
                onEnter={(option) => handleCategoryValueSelect(option)}
                onValueChange={setCategoryInputValue}
                placeholder="Type or select a category for this note"
                suggestions={suggestions}
                onCancel={() => {
                  setCategoryInputValue("");
                }}
                optionIsSelected={(() => {
                  if (!categorySelected) return false;
                  if (categorySelected.name !== categoryInputValue)
                    return false;
                  else return true;
                })()}
              />
            </InputWrapper>
          </BlockOptionsContainer>
        )}
      </Wrapper>
      <BottomDiv>
        <StyledButton onClick={() => modal.popModal()} inverted>
          Cancel
        </StyledButton>
        <StyledButton
          isLoading={loading}
          disable={checkBlockOptions()}
          onClick={handleBlockUsers}
        >
          Block
        </StyledButton>
      </BottomDiv>
    </>
  );
}
